import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, Button, IconButton, Modal, Fade, TextField, CircularProgress, Tabs, Tab, Snackbar,
  Grid, alpha, useTheme,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import RateReviewIcon from '@mui/icons-material/RateReviewRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopIcon from '@mui/icons-material/HourglassTopRounded';
import PersonOffIcon from '@mui/icons-material/PersonOffRounded';
import { motion } from 'framer-motion';
import {
  getAssignmentDetail, getSubmissionsForAssignment, getSubmissionDetail, reviewSubmission,
  getStudentSubmissionStatusList, getAssignmentPdfUrl,
} from '../api/teacherApi';
import EmptyState from '../components/EmptyState';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp } from '../utils/motion';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MotionPaper = motion(Paper);
const MotionTableRow = motion(TableRow);
const MotionBox = motion(Box);

const statusMeta = (status) => {
  const s = (status || 'submitted').toLowerCase();
  if (s === 'reviewed') return { color: tokens.mint, icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
  return { color: tokens.amber, icon: <HourglassTopIcon sx={{ fontSize: 16 }} /> };
};

const Submissions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { assignmentId } = useParams();

  const [assignment, setAssignment] = useState(null);
  const [tab, setTab] = useState(0); // 0 = Submissions, 1 = Student status

  const [submissions, setSubmissions] = useState([]);
  const [studentStatus, setStudentStatus] = useState({ submitted: [], not_submitted: [] });
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [score, setScore] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadAssignment = async () => {
    try {
      const res = await getAssignmentDetail(assignmentId);
      setAssignment(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const res = await getSubmissionsForAssignment(assignmentId);
      const data = res.data;
      setSubmissions(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // NOTE: response shape for this endpoint is assumed as
  // { submitted: [...], not_submitted: [...] } — adjust once confirmed.
  const loadStudentStatus = async () => {
    setLoading(true);
    try {
      const res = await getStudentSubmissionStatusList(assignmentId);
      const data = res.data || {};
      setStudentStatus({
        submitted: data.submitted || [],
        not_submitted: data.not_submitted || data.pending || [],
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAssignment(); }, [assignmentId]);
  useEffect(() => {
    if (tab === 0) loadSubmissions();
    else loadStudentStatus();
  }, [tab, assignmentId]);

  const handleViewDetail = async (submissionId) => {
    try {
      const res = await getSubmissionDetail(submissionId);
      setSelectedDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenReview = (submission) => {
    setReviewTarget(submission);
    setScore(submission.score ?? '');
    setReviewNote(submission.review ?? '');
    setReviewOpen(true);
  };

  const handleSaveReview = async () => {
    if (score === '') {
      setSnackbar({ open: true, message: 'Please enter a score.', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      await reviewSubmission(reviewTarget.id, { score, review: reviewNote });
      setSnackbar({ open: true, message: 'Review saved!', severity: 'success' });
      setReviewOpen(false);
      loadSubmissions();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to save review.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <IconButton onClick={() => navigate('/teacher/assignments')}><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h5">{assignment?.title || 'Submissions'}</Typography>
          {assignment?.course_name && (
            <Typography variant="body2" color="text.secondary">
              {assignment.course_name}{assignment.batch_name && ` — ${assignment.batch_name}`}
            </Typography>
          )}
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 2.5, minHeight: 40,
          '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
          '& .MuiTab-root': { minHeight: 40, fontWeight: 600 },
        }}
      >
        <Tab label="Submissions" />
        <Tab label="Student status" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : tab === 0 ? (
        submissions.length > 0 ? (
          <Paper elevation={0} sx={{ borderRadius: '18px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}>
                    <TableCell>S.No</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((s, i) => {
                    const meta = statusMeta(s.status);
                    return (
                      <MotionTableRow
                        key={s.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(i * 0.03, 0.6), duration: 0.3 }}
                        sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{s.student_name || s.admission_no || `#${s.admission || s.admission_id}`}</TableCell>
                        <TableCell>
                          <Chip
                            size="small" icon={meta.icon} label={s.status || 'Submitted'}
                            sx={{ backgroundColor: alpha(meta.color, 0.14), color: meta.color, textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{s.score ?? '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleViewDetail(s.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="primary" onClick={() => handleOpenReview(s)}>
                            <RateReviewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </MotionTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <EmptyState
            icon={<RateReviewIcon />}
            title="No submissions yet"
            subtitle="Once students submit, they'll show up here for review."
          />
        )
      ) : (
        <Grid container spacing={2} component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
          <Grid item xs={12} sm={6}>
            <MotionPaper variants={fadeInUp} elevation={0} sx={{ p: 2.5, borderRadius: '18px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon fontSize="small" sx={{ color: tokens.mint }} /> Submitted ({studentStatus.submitted.length})
              </Typography>
              {studentStatus.submitted.length > 0 ? studentStatus.submitted.map((st, i) => (
                <Typography key={st.admission_id || st.id || i} variant="body2" sx={{ py: 0.5 }}>
                  {st.student_name || st.name || `Admission #${st.admission_id || st.admission}`}
                </Typography>
              )) : <Typography variant="body2" color="text.secondary">No one yet.</Typography>}
            </MotionPaper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MotionPaper variants={fadeInUp} elevation={0} sx={{ p: 2.5, borderRadius: '18px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonOffIcon fontSize="small" sx={{ color: tokens.coral }} /> Not submitted ({studentStatus.not_submitted.length})
              </Typography>
              {studentStatus.not_submitted.length > 0 ? studentStatus.not_submitted.map((st, i) => (
                <Typography key={st.admission_id || st.id || i} variant="body2" sx={{ py: 0.5 }}>
                  {st.student_name || st.name || `Admission #${st.admission_id || st.admission}`}
                </Typography>
              )) : <Typography variant="body2" color="text.secondary">Everyone has submitted.</Typography>}
            </MotionPaper>
          </Grid>
        </Grid>
      )}

      {/* Submission detail modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} closeAfterTransition>
        <Fade in={detailOpen}>
          <MotionBox
            sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: { xs: '92vw', sm: 480 }, bgcolor: 'background.paper', borderRadius: '18px',
              boxShadow: 24, p: 3, outline: 'none',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">{selectedDetail?.student_name || 'Submission'}</Typography>
              <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
            </Box>
            {selectedDetail?.note && <Typography variant="body2" sx={{ mb: 1 }}>{selectedDetail.note}</Typography>}
            {selectedDetail?.score != null && <Typography variant="body2" sx={{ fontWeight: 600 }}>Score: {selectedDetail.score}</Typography>}
            {selectedDetail?.review && <Typography variant="body2">Review: {selectedDetail.review}</Typography>}
            {selectedDetail?.pdf_file && (
              <Button
                sx={{ mt: 2 }} variant="outlined"
                href={getAssignmentPdfUrl(selectedDetail.pdf_file)} target="_blank" rel="noopener noreferrer"
              >
                View submitted file
              </Button>
            )}
          </MotionBox>
        </Fade>
      </Modal>

      {/* Review modal */}
      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} closeAfterTransition>
        <Fade in={reviewOpen}>
          <MotionBox
            sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: { xs: '92vw', sm: 420 }, bgcolor: 'background.paper', borderRadius: '18px',
              boxShadow: 24, p: 3, outline: 'none',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Review submission</Typography>
              <IconButton onClick={() => setReviewOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <TextField
              label="Score" type="number" fullWidth sx={{ mb: 2 }}
              value={score} onChange={(e) => setScore(e.target.value)}
            />
            <TextField
              label="Review / feedback" fullWidth multiline rows={3} sx={{ mb: 2 }}
              value={reviewNote} onChange={(e) => setReviewNote(e.target.value)}
            />
            <Button variant="contained" fullWidth onClick={handleSaveReview} disabled={saving} sx={{ py: 1.1 }}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save review'}
            </Button>
          </MotionBox>
        </Fade>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Submissions;
